import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, Eye, EyeOff, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Kein gültiger Reset-Token gefunden");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Passwort muss mindestens 6 Zeichen haben");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwörter stimmen nicht überein");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Zurücksetzen des Passworts");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="MargeVio" className="h-20 mx-auto mb-4" />
          <p className="text-slate-400 mt-2">Gastro Kalkulator</p>
        </div>

        <div className="bg-white/95 backdrop-blur-lg border border-slate-200 p-8 shadow-2xl rounded-xl">
          {success ? (
            /* Success State */
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Passwort geändert!</h2>
              <p className="text-slate-500 text-sm mb-6">
                Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6"
                data-testid="goto-login-btn"
              >
                Zur Anmeldung
              </Button>
            </div>
          ) : !token ? (
            /* No Token State */
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle size={32} className="text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Ungültiger Link</h2>
              <p className="text-slate-500 text-sm mb-6">
                Dieser Reset-Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an.
              </p>
              <Button
                onClick={() => navigate("/login")}
                variant="outline"
                className="w-full py-6"
              >
                <ArrowLeft size={18} className="mr-2" />
                Zurück zur Anmeldung
              </Button>
            </div>
          ) : (
            /* Reset Form */
            <>
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <KeyRound size={24} className="text-blue-600" />
                </div>
                <h2 className="font-display text-xl font-semibold text-slate-800">Neues Passwort setzen</h2>
                <p className="text-sm text-slate-500 mt-1">Geben Sie Ihr neues Passwort ein</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Neues Passwort</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mindestens 6 Zeichen"
                      required
                      className="border-slate-300 rounded-lg py-5 pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Passwort bestätigen</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Passwort wiederholen"
                    required
                    className="border-slate-300 rounded-lg py-5"
                    data-testid="confirm-password-input"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="reset-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-6"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Wird gespeichert..." : "Passwort ändern"}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate("/login")}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  <ArrowLeft size={14} className="inline mr-1" />
                  Zurück zur Anmeldung
                </button>
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-6">
          <p className="text-xs text-slate-500">Gastro Kalkulator</p>
          <p className="text-xs text-violet-400 font-medium">by Gian-Luca</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
